// help-entries.ts — THE help-menu IA, as data, for every surface that opens
// a help menu (owner request 2026-09-07: "under the help menu, we need to add
// a link to the Release Notes by the options for the API Documentation and
// the Knowledgebase").
//
// WHY IT LIVES HERE. There are exactly two help MENUS in the product and they
// were duplicated registers of the same IA:
//   helm-console/src/client/help-menu.ts   the Bridge header's `?` popover
//   helm-bridge/src/client/rail.ts         the Helm rail's bottom-left `?`
// Measured before this file existed: the two entries (Knowledgebase, API
// documentation) were written out twice, once as a frozen table and once as
// hand-written menu HTML, with two separate suites pinning "EXACTLY the two
// entries". Adding a third entry therefore had to be done twice, and the next
// one would too — the append-collision shape this framework keeps paying for.
// pi-stack/src/client is the SHARED client layer both hosts already compile
// against (buttons.ts, confirm.ts, form-controls.ts moved here for the same
// reason), so ONE module is consumable by both without a copy lane and
// without a byte-identity gate to police.
//
// CONTRACT:
//   - import-free leaf. It must stay value-import-free so either bundle can
//     take it standalone (the themes.ts portability rule).
//   - `glyph` is the field name ON PURPOSE. helm-bridge/scripts/
//     check-icon-refs.mjs collects `glyph:` on EVERY root including this one
//     (an `icon:` table here would be skipped — pi's chat surfaces carry
//     inline icon tables that never touch the sprite). Naming the field
//     `glyph` is what makes these ids visible to icons:check.
//   - hrefs are ROOT-RELATIVE paths on the console apex. The Bridge header
//     uses them verbatim; the Helm rail prefixes them with the derived apex
//     (its host minus the first label on a release subdomain).

export interface HelpMenuEntry {
  /** Menu label, rendered verbatim. */
  label: string;
  /** Root-relative path on the console apex (see the apex note above). */
  href: string;
  /** Sprite id — see the `glyph` note above (icons:check reads this field). */
  glyph: string;
}

/** The IA, in order. Both suites pin EXACTLY these three, in this order.
 *
 * Release Notes points at the KB portal's own release-notes view
 * (`/help/#/release-notes` — portal-main.ts's `releaseNotesHash()` route,
 * which reads the workflow-minted kb/release-notes/versions.json through
 * GET /api/help/release-notes). Never an S3 URL: the notes are a session-gated
 * data plane, and the portal already renders the index newest-first. */
export const HELP_MENU_ENTRIES: readonly HelpMenuEntry[] = Object.freeze([
  { label: 'Knowledgebase', href: '/help/', glyph: 'book-open' },
  { label: 'API documentation', href: '/help/api/', glyph: 'square-terminal' },
  { label: 'Release Notes', href: '/help/#/release-notes', glyph: 'scroll-text' },
]);

/** The path a help href actually REQUESTS — the fragment is a client-side
 * route and never reaches the server. Both suites use this to assert that
 * every entry resolves to a path the console's portal router serves. */
export function helpEntryPath(href: string): string {
  const hash = href.indexOf('#');
  return hash === -1 ? href : href.slice(0, hash);
}
